import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Skeleton,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

/** Tokens visuales del diseño "Clientes y Cobranza · Prami". */
export const COLORS = {
  primary: "#161E55",
  primaryHover: "#0F1640",
  accent: "#A8C8AF",
  page: "#F5F6F8",
  border: "#E5E7EB",
  text: "#1D2939",
  muted: "#475467",
  danger: "#B42318",
  dangerSolid: "#DC4A41",
  rowHover: "#F9FAFB",
};

const BADGE_TONES = {
  green: { background: "#ECFDF3", color: "#05603A", dot: "#12B76A" },
  yellow: { background: "#FEF7C3", color: "#7A5200", dot: "#EAB308" },
  orange: { background: "#FFEFD6", color: "#93370D", dot: "#F97316" },
  red: { background: "#FEE4E2", color: "#B42318", dot: "#D92D20" },
  blue: { background: "#E8EEFF", color: "#1D3FA8", dot: "#2563EB" },
  purple: { background: "#F4EBFF", color: "#53389E", dot: "#7F56D9" },
  gray: { background: "#F2F4F7", color: "#344054", dot: "#98A2B3" },
};

export const Card = styled(Paper)({
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  boxShadow: "none",
  backgroundColor: "#fff",
});

const baseButton = {
  minHeight: 44,
  padding: "0 16px",
  borderRadius: 10,
  fontWeight: 500,
  fontSize: 14,
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
};

export const SecondaryButton = styled(Button)({
  ...baseButton,
  color: COLORS.primary,
  backgroundColor: "#fff",
  border: "1px solid #D0D5DD",
  "&:hover": { backgroundColor: COLORS.rowHover, boxShadow: "none" },
});

export const PrimaryButton = styled(Button)({
  ...baseButton,
  color: "#fff",
  backgroundColor: COLORS.primary,
  "&:hover": { backgroundColor: COLORS.primaryHover, boxShadow: "none" },
  "&.Mui-disabled": { color: "#fff", backgroundColor: "#98A2B3" },
});

export const DangerButton = styled(Button)({
  ...baseButton,
  color: "#fff",
  backgroundColor: COLORS.dangerSolid,
  "&:hover": { backgroundColor: "#C93C34", boxShadow: "none" },
  "&.Mui-disabled": { color: "#fff", backgroundColor: "#FDA29B" },
});

export const headCellSx = {
  fontSize: 14,
  fontWeight: 500,
  color: COLORS.text,
  textAlign: "center",
  whiteSpace: "nowrap",
  borderTop: `1px solid ${COLORS.border}`,
};

export const cellSx = {
  textAlign: "center",
  fontSize: 14,
  fontWeight: 500,
  fontVariantNumeric: "tabular-nums",
  color: COLORS.text,
};

export const linkSx = {
  color: COLORS.primary,
  fontWeight: 500,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

export const labelSx = { fontSize: 14, fontWeight: 500, color: COLORS.muted };

export function StatusBadge({ tone = "gray", label, sx }) {
  const palette = BADGE_TONES[tone] || BADGE_TONES.gray;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 500,
        whiteSpace: "nowrap",
        backgroundColor: palette.background,
        color: palette.color,
        "&::before": {
          content: '""',
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: palette.dot,
        },
        ...sx,
      }}
    >
      {label}
    </Box>
  );
}

export function CountPill({ value, sx }) {
  return (
    <Box
      component="span"
      sx={{
        minWidth: 26,
        height: 26,
        px: 1,
        borderRadius: 999,
        backgroundColor: "#EEF1F6",
        color: "#344054",
        fontWeight: 500,
        fontSize: 14,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      {value}
    </Box>
  );
}

/** Ancho máximo del contenido: ocupa todo el espacio disponible y solo se centra en pantallas muy grandes. */
export const CONTENT_MAX_WIDTH = 1680;

/**
 * Contenedor de página. HomeAdmin pone el menú lateral y la página en una fila flex, así que
 * la página debe crecer (`flex: 1`) y poder encogerse (`minWidth: 0`) para que las tablas
 * hagan scroll dentro de su tarjeta en vez de desbordar la pantalla.
 */
export function PageContainer({ children }) {
  return (
    <Box
      component="main"
      sx={{
        flex: "1 1 auto",
        minWidth: 0,
        width: "100%",
        backgroundColor: COLORS.page,
        minHeight: "100vh",
        pt: { xs: "88px", md: "120px" },
        pb: { xs: 4, md: 8 },
        px: { xs: 1.5, sm: 2, md: 4, xl: 6 },
        color: COLORS.text,
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0, maxWidth: CONTENT_MAX_WIDTH, mx: "auto", display: "flex", flexDirection: "column", gap: { xs: 2, md: 3 } }}>
        {children}
      </Box>
    </Box>
  );
}

/** Ruta de navegación. `items`: [{ label, to? }]; el último es la página actual. */
export function Breadcrumbs({ items }) {
  return (
    <Box
      component="nav"
      aria-label="Ruta de navegación"
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, fontSize: 14 }}
    >
      {items.map((item, index) => (
        <React.Fragment key={item.label}>
          {index > 0 && (
            <Box component="span" aria-hidden="true" sx={{ color: COLORS.muted }}>
              ›
            </Box>
          )}
          {item.to ? (
            <Box component={RouterLink} to={item.to} sx={{ ...linkSx, fontWeight: 500 }}>
              {item.label}
            </Box>
          ) : (
            <Box component="span" aria-current="page" sx={{ fontWeight: 700 }}>
              {item.label}
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

export function SectionTitle({ children, component = "h2", sx }) {
  return (
    <Typography
      component={component}
      sx={{ m: 0, fontSize: 14, fontWeight: 700, color: COLORS.primary, ...sx }}
    >
      {children}
    </Typography>
  );
}

export function SkeletonRows({ columns, rows = 6 }) {
  return Array.from({ length: rows }, (_, rowIndex) => (
    <TableRow key={rowIndex}>
      {Array.from({ length: columns }, (__, cellIndex) => (
        <TableCell key={cellIndex}>
          <Skeleton variant="rounded" animation="wave" height={18} />
        </TableCell>
      ))}
    </TableRow>
  ));
}

/** Mensaje centrado para estados vacíos o de error dentro de una tabla. */
export function TableMessage({ columns, title, description, action, role, titleSx }) {
  return (
    <TableRow>
      <TableCell colSpan={columns} sx={{ py: 7, textAlign: "center", borderBottom: 0 }}>
        <Box role={role} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary, ...titleSx }}>{title}</Typography>
          {description && <Typography sx={{ color: COLORS.muted }}>{description}</Typography>}
          {action}
        </Box>
      </TableCell>
    </TableRow>
  );
}

const PHONE_MEDIA_QUERY = "(max-width:599.95px)";

/** Contenedor común de los modales del módulo. */
export function ModalShell({ open, onClose, title, subtitle, children, actions, maxWidth = "sm" }) {
  // La app no tiene ThemeProvider: se usa la media query literal (breakpoint "sm" de MUI = 600px).
  const isPhone = useMediaQuery(PHONE_MEDIA_QUERY);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isPhone}
      maxWidth={maxWidth}
      aria-labelledby="gestion-cartera-modal-title"
      PaperProps={{ sx: { borderRadius: isPhone ? 0 : "16px", p: { xs: 0, sm: 1 } } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, px: 3, pt: 2.5 }}>
        <Box>
          <Typography
            id="gestion-cartera-modal-title"
            component="h2"
            sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary, lineHeight: 1.25 }}
          >
            {title}
          </Typography>
          {subtitle && <Typography sx={{ mt: 0.5, color: COLORS.muted }}>{subtitle}</Typography>}
        </Box>
        <IconButton aria-label="Cerrar" onClick={onClose} sx={{ width: 44, height: 44 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: "20px !important" }}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, gap: 1, borderTop: "1px solid #EAECF0" }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}
